/**
 * Feedback Service
 * Handles user feedback collection and retrieval with image upload support
 */

import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage } from '../config/firebase';

const FEEDBACK_COLLECTION = 'feedback';
const FEEDBACK_IMAGES_PATH = 'feedback-images';
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_IMAGES = 3;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

export type FeedbackType = 'bug' | 'feature' | 'improvement' | 'general' | 'praise';
export type FeedbackStatus = 'new' | 'reviewed' | 'in_progress' | 'resolved' | 'closed';
export type FeedbackPriority = 'low' | 'medium' | 'high' | 'critical';

export interface FeedbackImage {
  url: string;
  path: string; // Storage path for deletion
  name: string;
}

export interface Feedback {
  id: string;
  type: FeedbackType;
  title: string;
  description: string;
  userEmail?: string;
  userName?: string;
  userId?: string;
  isAnonymous?: boolean; // Flag indicating anonymous submission
  status: FeedbackStatus;
  priority?: FeedbackPriority;
  adminNotes?: string;
  page?: string; // Which page the feedback was submitted from
  userAgent?: string;
  images?: FeedbackImage[]; // Up to 2 images for bug, improvement, general
  createdAt: string;
  updatedAt?: string;
  resolvedAt?: string;
}

export interface FeedbackFormData {
  type: FeedbackType;
  title: string;
  description: string;
  email?: string;
  images?: File[]; // Files to upload
  priority?: FeedbackPriority; // Optional priority
}

// Types that support image uploads
export const IMAGE_SUPPORTED_TYPES: FeedbackType[] = ['bug', 'improvement', 'general'];

class FeedbackService {
  /**
   * Validate image file
   */
  validateImage(file: File): { valid: boolean; error?: string } {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      return { valid: false, error: `Invalid file type. Allowed: JPEG, PNG, GIF, WebP` };
    }
    if (file.size > MAX_IMAGE_SIZE) {
      return { valid: false, error: `File too large. Maximum size: 5MB` };
    }
    return { valid: true };
  }

  /**
   * Upload images to Firebase Storage
   */
  async uploadImages(files: File[], feedbackId: string): Promise<FeedbackImage[]> {
    const uploadedImages: FeedbackImage[] = [];

    // Limit to MAX_IMAGES
    const filesToUpload = files.slice(0, MAX_IMAGES);

    for (const file of filesToUpload) {
      const validation = this.validateImage(file);
      if (!validation.valid) {
        console.warn(`Skipping invalid image: ${validation.error}`);
        continue;
      }

      const timestamp = Date.now();
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const storagePath = `${FEEDBACK_IMAGES_PATH}/${feedbackId}/${timestamp}_${sanitizedName}`;
      const storageRef = ref(storage, storagePath);

      try {
        await uploadBytes(storageRef, file);
        const downloadUrl = await getDownloadURL(storageRef);

        uploadedImages.push({
          url: downloadUrl,
          path: storagePath,
          name: file.name,
        });
      } catch (error) {
        console.error(`Failed to upload image ${file.name}:`, error);
      }
    }

    return uploadedImages;
  }

  /**
   * Delete images from Firebase Storage
   */
  async deleteImages(images: FeedbackImage[]): Promise<void> {
    for (const image of images) {
      try {
        const storageRef = ref(storage, image.path);
        await deleteObject(storageRef);
      } catch (error) {
        console.error(`Failed to delete image ${image.name}:`, error);
      }
    }
  }

  /**
   * Submit new feedback with optional images
   */
  async submitFeedback(
    data: FeedbackFormData,
    userInfo?: { email?: string; name?: string; userId?: string }
  ): Promise<string> {
    const isAnonymous = userInfo?.name === 'Anonymous';

    // userId must always be auth.uid to satisfy Firestore security rules
    // For anonymous: strip email/name but keep userId for auth validation
    const { getAuth } = await import('firebase/auth');
    const authUid = getAuth().currentUser?.uid || userInfo?.userId || '';

    const feedbackData: Record<string, unknown> = {
      type: data.type,
      title: data.title.trim(),
      description: data.description.trim(),
      userEmail: isAnonymous ? '' : (data.email || userInfo?.email || ''),
      userName: isAnonymous ? 'Anonymous' : (userInfo?.name || ''),
      userId: authUid,
      isAnonymous,
      status: 'new' as FeedbackStatus,
      ...(data.priority ? { priority: data.priority } : {}),
      page: typeof window !== 'undefined' ? window.location.pathname : '',
      userAgent: isAnonymous ? '' : (typeof navigator !== 'undefined' ? navigator.userAgent : ''),
      createdAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, FEEDBACK_COLLECTION), feedbackData);

    // Upload images if provided and type supports it
    if (data.images && data.images.length > 0 && IMAGE_SUPPORTED_TYPES.includes(data.type)) {
      const uploadedImages = await this.uploadImages(data.images, docRef.id);

      if (uploadedImages.length > 0) {
        // Update the document with image URLs
        await updateDoc(doc(db, FEEDBACK_COLLECTION, docRef.id), {
          images: uploadedImages,
        });
      }
    }

    return docRef.id;
  }

  /**
   * Get all feedback (admin only)
   */
  async getAllFeedback(): Promise<Feedback[]> {
    const feedbackRef = collection(db, FEEDBACK_COLLECTION);
    const q = query(feedbackRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);

    const feedback: Feedback[] = [];
    snapshot.forEach((doc) => {
      const data = doc.data();

      // Convert Firestore timestamps to ISO strings
      let createdAt = data.createdAt;
      if (createdAt instanceof Timestamp) {
        createdAt = createdAt.toDate().toISOString();
      } else if (!createdAt) {
        createdAt = new Date().toISOString();
      }

      let updatedAt = data.updatedAt;
      if (updatedAt instanceof Timestamp) {
        updatedAt = updatedAt.toDate().toISOString();
      }

      let resolvedAt = data.resolvedAt;
      if (resolvedAt instanceof Timestamp) {
        resolvedAt = resolvedAt.toDate().toISOString();
      }

      feedback.push({
        id: doc.id,
        type: data.type || 'general',
        title: data.title || '',
        description: data.description || '',
        userEmail: data.userEmail,
        userName: data.userName,
        userId: data.userId,
        isAnonymous: data.isAnonymous || false,
        status: data.status || 'new',
        priority: data.priority,
        adminNotes: data.adminNotes,
        page: data.page,
        userAgent: data.userAgent,
        images: data.images || undefined,
        createdAt,
        updatedAt,
        resolvedAt,
      });
    });

    return feedback;
  }

  /**
   * Update feedback status/notes (admin only)
   */
  async updateFeedback(
    feedbackId: string,
    updates: Partial<Pick<Feedback, 'status' | 'priority' | 'adminNotes'>>
  ): Promise<void> {
    const feedbackRef = doc(db, FEEDBACK_COLLECTION, feedbackId);

    const updateData: any = {
      ...updates,
      updatedAt: serverTimestamp(),
    };

    // If status is resolved or closed, set resolvedAt
    if (updates.status === 'resolved' || updates.status === 'closed') {
      updateData.resolvedAt = serverTimestamp();
    }

    await updateDoc(feedbackRef, updateData);
  }

  /**
   * Delete feedback and associated images (admin only)
   */
  async deleteFeedback(feedbackId: string, images?: FeedbackImage[]): Promise<void> {
    // Delete images from storage if provided
    if (images && images.length > 0) {
      await this.deleteImages(images);
    }

    // Delete the feedback document
    const feedbackRef = doc(db, FEEDBACK_COLLECTION, feedbackId);
    await deleteDoc(feedbackRef);
  }

  /**
   * Get feedback stats
   */
  async getFeedbackStats(): Promise<{
    total: number;
    byStatus: Record<FeedbackStatus, number>;
    byType: Record<FeedbackType, number>;
    newThisWeek: number;
  }> {
    const feedback = await this.getAllFeedback();

    const stats = {
      total: feedback.length,
      byStatus: {
        new: 0,
        reviewed: 0,
        in_progress: 0,
        resolved: 0,
        closed: 0,
      } as Record<FeedbackStatus, number>,
      byType: {
        bug: 0,
        feature: 0,
        improvement: 0,
        general: 0,
        praise: 0,
      } as Record<FeedbackType, number>,
      newThisWeek: 0,
    };

    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    feedback.forEach((f) => {
      stats.byStatus[f.status] = (stats.byStatus[f.status] || 0) + 1;
      stats.byType[f.type] = (stats.byType[f.type] || 0) + 1;

      if (new Date(f.createdAt) >= oneWeekAgo) {
        stats.newThisWeek++;
      }
    });

    return stats;
  }
}

export const feedbackService = new FeedbackService();
